import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, ChevronDown } from 'lucide-react';
import { demoDomains, demoUrlFor, domainSummaries, type DemoDomain } from '../../data/demoCenter';
import { clinicalCurrentScope, clinicalFutureCaveat, clinicalFutureOptions, domainDemonstrates, separateWorkspacesCopy } from '../../data/noetivra';
import { fadeUp } from '../../utils/motion';
import { DomainIcon } from '../demoCenter/DomainIcon';
import { DemoStatusBadge } from '../demoCenter/DemoStatusBadge';
import { FutureUses } from '../layout/FutureUses';
import { TalentDetails } from './TalentDetails';

/** "What each workspace demonstrates": one entry per domain, with its own anchor. */
export function NoetivraDomainShowcase() {
  return <section aria-labelledby="demonstrates-title" className="bg-white border-b border-line py-20 lg:py-28" data-id="element-1528" data-mp="2otym7zi89oqz">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1529" data-mp="3hkgq32qrzu5g">
        <motion.h2 {...fadeUp} id="demonstrates-title" className="font-heading font-bold text-3xl lg:text-4xl text-ink-dark tracking-tight leading-tight max-w-2xl mb-6" data-id="element-1530" data-mp="3hj5lj0piaida">
          What each workspace demonstrates
        </motion.h2>
        <motion.p {...fadeUp} className="max-w-3xl text-lg text-ink-muted leading-relaxed mb-12" data-id="element-1531" data-mp="2sfe6ua6zv8xg">
          Each domain is configured for its own participants, records and responsibilities. Successful demo
          interactions show how a workflow behaves; they are not evidence that an assessment method works.
        </motion.p>

        <div className="border-b border-line" data-id="element-1532" data-mp="2ninn9cvzdv7a">
          {demoDomains.map(domain => <DomainEntry key={domain.id} domain={domain} data-id="element-1533" data-mp="29jea6m32nh7j" />)}
        </div>
      </div>
    </section>;
}
function DomainEntry({
  domain
}: {
  domain: DemoDomain;
}) {
  const titleId = `${domain.id}-title`;
  return <article id={domain.id} aria-labelledby={titleId} className="scroll-mt-24 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16 border-t border-line py-10" data-id="element-1534" data-mp="7odpe0ol0lf8">
      <div className="lg:col-span-4" data-id="element-1535" data-mp="b9fhmxyppgsk">
        <div className="flex items-center gap-3" data-id="element-1536" data-mp="69axmxyli3xl">
          <DomainIcon id={domain.id} data-id="element-1537" data-mp="67gf1qasqnwo" />
          {domain.status === 'scoped' && <DemoStatusBadge status={domain.status} data-id="element-1538" data-mp="1955lgoeyfez" />}
        </div>
        <h3 id={titleId} className="mt-4 font-heading font-bold text-2xl text-ink-dark leading-snug" data-id="element-1539" data-mp="3sg99i2p9hlj6">
          {domain.title}
        </h3>
        <p className="mt-1 text-sm font-medium text-lavender" data-id="element-1540" data-mp="txkfjosou7jg">{domain.solutionLabel}</p>
        <p className="mt-3 text-sm text-ink-muted" data-id="element-1541" data-mp="oz963f6b1z1r">{domain.workspaces.join(' · ')}</p>
        <div className="mt-5 flex flex-col items-start gap-2" data-id="element-1542" data-mp="k8alw4i74gt8">
          <a href={demoUrlFor(domain.id)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-semibold text-navy hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-1543" data-mp="k6pv8pz05i0n">
            {domain.actionLabel}
            <ArrowUpRight size={16} aria-hidden="true" data-id="element-1544" data-mp="1g710ddssaozp" />
            <span className="sr-only" data-id="element-1545" data-mp="1g8luj2b6s5x1">(opens in a new tab)</span>
          </a>
          {domain.relatedRoute && <a href={domain.relatedRoute.href} className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-muted hover:text-navy transition-colors duration-150 whitespace-nowrap" data-id="element-1546" data-mp="1kap6rt5ojejd">
              {domain.relatedRoute.label} <ArrowRight size={14} aria-hidden="true" data-id="element-1547" data-mp="1z4u3oh7bl0xm" />
            </a>}
        </div>
      </div>

      <div className="lg:col-span-8" data-id="element-1548" data-mp="2365tamq71m5t">
        <p className="text-xl text-ink-dark leading-relaxed" data-id="element-1549" data-mp="235483pfgrjad">{domainSummaries[domain.id]}</p>
        <ul className="mt-5 space-y-2" data-id="element-1550" data-mp="2809zv4vjrs6d">
          {domainDemonstrates[domain.id].map(item => <li key={item} className="flex gap-3 text-ink-muted leading-relaxed" data-id="element-1551" data-mp="2cz4j27xffir0">
              <span aria-hidden="true" className="mt-2.5 h-1 w-3 shrink-0 bg-teal" data-id="element-1552" data-mp="2hy7o3sdepoyz" />
              {item}
            </li>)}
        </ul>
        {domain.note && <p className="mt-5 border-l-4 border-teal pl-5 text-ink-dark leading-relaxed" data-id="element-1553" data-mp="3jm2xtti92ns9">{domain.note}</p>}
        <DomainExtras id={domain.id} data-id="element-1554" data-mp="3emqdbfeupssu" />
      </div>
    </article>;
}
function DomainExtras({
  id
}: {
  id: DemoDomain['id'];
}) {
  if (id === 'government') {
    return <div className="mt-6 rounded-lg bg-surface-bg p-5" data-id="element-1555" data-mp="3odteeh7s67s4">
        <p id="workspaces-title" className="scroll-mt-24 font-heading font-semibold text-ink-dark" data-id="element-1556" data-mp="3oecudsdvem3i">
          Separate workspaces, connected workflows.
        </p>
        <p className="mt-2 text-ink-muted leading-relaxed" data-id="element-1557" data-mp="2vpxj9gxv0nxg">{separateWorkspacesCopy}</p>
        <a href="/government#workspaces" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-1558" data-mp="39p6q3q06e5or">
          More on the Government workspaces <ArrowRight size={14} aria-hidden="true" data-id="element-1559" data-mp="p0iui4qn6cov" />
        </a>
      </div>;
  }
  if (id === 'talent') {
    return <div className="mt-8 space-y-6" data-id="element-1560" data-mp="sgn6g9txczw5">
        <TalentDetails data-id="element-1561" data-mp="sfkgc44ff1ha" />
        <FutureUses area="talent" data-id="element-1562" data-mp="sf2dgon9dp0e" />
      </div>;
  }
  if (id === 'leadership') return <FutureUses area="leadership" className="mt-6" data-id="element-1563" data-mp="xh1dppnpc5t4" />;
  if (id === 'clinical') {
    return <div className="mt-6 space-y-4" data-id="element-1564" data-mp="ijqutv8qbxnd">
        <p className="text-ink-dark leading-relaxed" data-id="element-1565" data-mp="nhakrseshyv5">{clinicalCurrentScope}</p>
        <details className="group rounded-lg border border-line" data-id="element-1566" data-mp="njd8g5sgzfwp">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 font-heading font-semibold text-ink-dark hover:bg-surface-bg transition-colors duration-150 rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal" data-id="element-1567" data-mp="1b867ib4e23vj">
            Future research and integration options
            <ChevronDown size={18} aria-hidden="true" className="shrink-0 text-ink-muted transition-transform duration-200 group-open:rotate-180" data-id="element-1568" data-mp="16e1y7l7nhjra" />
          </summary>
          <div className="space-y-4 px-5 pb-5" data-id="element-1569" data-mp="1yx7ju9gcdz63">
            {clinicalFutureOptions.map(option => <p key={option} className="text-ink-muted leading-relaxed" data-id="element-1570" data-mp="28rwbzxs72h5v">
                {option}
              </p>)}
            <p className="text-ink-dark leading-relaxed" data-id="element-1571" data-mp="23rif9bw1mtn5">{clinicalFutureCaveat}</p>
            <FutureUses area="clinical" data-id="element-1572" data-mp="1zpww58sx1j4i" />
          </div>
        </details>
      </div>;
  }
  return null;
}